/* ============================================================
   Lab Effects — le-sheet-lock.js
   ONE shared background scroll-lock for every slide-up / sheet /
   drawer / modal on the site. Loaded globally from theme.liquid.

   WHY THIS EXISTS
   Each sheet used to roll its own `document.body.style.overflow`
   lock. Three problems with that:

     1. body-only. document.scrollingElement here is <html>, so
        locking body alone leaves wheel/keyboard scrolling of the
        document alive in several browsers — the background slid
        around under the open sheet.
     2. No ref-counting. Sheets can overlap (quick-add opened from
        inside the collection About sheet; a mega-menu closing while
        a sheet is up). Whichever closed FIRST wrote overflow=''
        and silently unlocked the one still open.
     3. No scroll restoration or scrollbar compensation, so locking
        could shift the page horizontally as the scrollbar vanished.

   USAGE
     LESheetLock.lock();      // on open
     LESheetLock.unlock();    // on close  (ref-counted, pairs with lock)
     LESheetLock.reset();     // hard release — navigation / teardown only

   Every lock() must be matched by exactly one unlock(). Use reset()
   when you cannot guarantee that (e.g. wiping state on page change).

   While locked, <html> carries `.le-sheet-locked`, which is also the
   hook the scrim blur rules in lab-effects.css hang off.
   ============================================================ */
(function () {
  'use strict';

  var html = document.documentElement;
  var depth = 0;
  var savedY = 0;
  var savedBodyPadRight = '';

  function scrollbarWidth() {
    return Math.max(0, window.innerWidth - html.clientWidth);
  }

  function lock() {
    // Already locked by another sheet — just take a reference and leave
    // the existing lock (and its saved scroll position) alone.
    if (depth++ > 0) return;

    savedY = window.scrollY || window.pageYOffset || 0;

    // Removing the scrollbar widens the viewport and shifts the page
    // sideways; pad it back by exactly the scrollbar width.
    var sbw = scrollbarWidth();
    savedBodyPadRight = document.body.style.paddingRight;
    if (sbw > 0) {
      var current = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = current + sbw + 'px';
    }

    // <html> ONLY -- never also <body>. CSS propagates the root element's
    // overflow to the VIEWPORT, so hidden here stops the page scrolling while
    // html itself stays a non-scroll-container.
    //
    // Setting it on <body> as well is what broke the navbar: body then becomes
    // a real scroll container, and `.le-navbar-section` (position:sticky)
    // anchors to THAT instead of the viewport. Body is never scrolled, so the
    // sticky nav resolved to the top of the document and vanished from view
    // whenever a panel opened on a scrolled page. Keep this to <html>.
    html.style.overflow = 'hidden';
    html.classList.add('le-sheet-locked');
  }

  function unlock(force) {
    if (depth === 0) return;
    if (force) { depth = 0; } else if (--depth > 0) { return; }
    depth = 0;

    html.style.overflow = '';
    document.body.style.paddingRight = savedBodyPadRight;
    html.classList.remove('le-sheet-locked');

    // Some browsers clamp scrollTop while overflow:hidden is applied.
    // Put the reader back exactly where they were before the sheet.
    if (Math.abs((window.scrollY || 0) - savedY) > 1) {
      var behavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';   // never animate the restore
      window.scrollTo(0, savedY);
      html.style.scrollBehavior = behavior;
    }
  }

  /* ============================================================
     WATCHDOG — repair a leaked Horizon body lock
     ------------------------------------------------------------
     Horizon's dialog.js (cart drawer, search modal, quick-add) locks the page
     by pinning `body { width:100%; position:fixed; top:-<scrollY>px }`.
     Two races can leave that pinned with nothing on screen:

       • showDialog() defers its body writes to requestAnimationFrame, but
         closeDialog() clears them synchronously after its await. Opening and
         closing inside a single frame -- someone smashing the cart button --
         therefore RE-APPLIES the lock *after* the cleanup already ran.
       • closeDialog() only unpins after `await onAnimationEnd(dialog)`. If that
         settles late (backgrounded tab, cancelled animation), the window widens.

     The page then looks frozen: nothing scrolls, and because body is fixed the
     sticky navbar strands at the bottom of the viewport.

     We do not edit Horizon's own JS (see CLAUDE.md), so instead watch for the
     impossible state -- body pinned while NO modal dialog is open -- and undo
     it, restoring the scroll offset that `top` still encodes.
     ============================================================ */

  function anyModalOpen() {
    // :modal is the precise test (only showModal() blocks page scroll), but it
    // is not in older browsers -- fall back to any open dialog there.
    try {
      return !!document.querySelector('dialog:modal');
    } catch (err) {
      return !!document.querySelector('dialog[open]');
    }
  }

  function repairLeakedBodyLock() {
    var b = document.body;
    if (!b || b.style.position !== 'fixed') return;  // not locked by Horizon
    if (anyModalOpen()) return;                      // legitimately locked
    if (depth > 0) return;                           // one of ours owns it

    // `top` still holds the negated scroll offset Horizon saved on open.
    var y = Math.abs(parseFloat(b.style.top || '0')) || 0;
    b.style.width = '';
    b.style.position = '';
    b.style.top = '';

    var behavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';              // never animate the repair
    window.scrollTo(0, y);
    html.style.scrollBehavior = behavior;
  }

  var repairTimer = null;
  function scheduleRepair() {
    if (repairTimer) clearTimeout(repairTimer);
    // Long enough not to fight a dialog that is still animating open/closed.
    repairTimer = setTimeout(repairLeakedBodyLock, 250);
  }

  function startWatchdog() {
    // 1. Any change to body's inline style (the lock going on or off).
    if (window.MutationObserver) {
      new MutationObserver(scheduleRepair).observe(document.body, {
        attributes: true,
        attributeFilter: ['style']
      });
    }
    // 2. Horizon announces its own closes.
    document.addEventListener('dialog:close', scheduleRepair);
    // 3. Self-heal the moment the customer touches anything. The string compare
    //    short-circuits, so this costs nothing on a normal page.
    document.addEventListener(
      'pointerdown',
      function () { if (document.body.style.position === 'fixed') scheduleRepair(); },
      { capture: true, passive: true }
    );
    // 4. Returning to a backgrounded tab, where animations never finished.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) scheduleRepair();
    });
    window.addEventListener('pageshow', scheduleRepair);
  }

  if (document.body) startWatchdog();
  else document.addEventListener('DOMContentLoaded', startWatchdog);

  window.LESheetLock = {
    lock: lock,
    repairLeakedBodyLock: repairLeakedBodyLock,
    unlock: function () { unlock(false); },
    reset: function () { unlock(true); },
    isLocked: function () { return depth > 0; },
    depth: function () { return depth; }
  };
})();
